import React, { useState } from "react";
import type { Card } from "@stackje/common/config";
import { EditHeroCard } from "./EditHeroCard";
import { EditTextCard } from "./EditTextCard";
import { EditLinkCard } from "./EditLinkCard";
import { EditImageCard } from "./EditImageCard";
import { EditFeatureCard } from "./EditFeatureCard";
import { EditFaqCard } from "./EditFaqCard";
import { EditWhatsappCard } from "./EditWhatsappCard";
import { EditTippingCard } from "./EditTippingCard";
import { TabsWithFab } from "@/components/shared/TabsWithFab";
import { useToast } from "@stackje/shared-components/hooks/useToast";
import { captureErrorAndToast } from "@/utils/error-utils";
import { useNavigate } from "@tanstack/react-router";

export type EditCardContentProps = {
  card: Card;
  cardIndex: number;
  onFormInputChange: () => void;
  onSubmit?: (formData: FormData) => Promise<void>;
};

export function EditCardContent({
  card,
  cardIndex,
  onFormInputChange,
  onSubmit,
}: EditCardContentProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const navigate = useNavigate();
  const { toast } = useToast();
  let formContent: React.ReactNode;
  let previewContent: React.ReactNode;
  let switchSubTypeSelection: (() => void) | undefined = undefined;
  let toggleTheme: () => void;
  let handleSubmitForm: (onSubmit: (data: Record<string, unknown>) => void | Promise<void>) => (e?: React.BaseSyntheticEvent) => Promise<void>;

  switch (card.type) {
    case "hero1":
    case "hero2":
    case "hero3":
    case "hero4":
    case "hero5": {
      const HeroEdit = EditHeroCard({ card, cardIndex });
      formContent = HeroEdit.formContent;
      previewContent = HeroEdit.previewContent;
      switchSubTypeSelection = HeroEdit.toggleHeroType;
      toggleTheme = HeroEdit.shuffleColors;
      handleSubmitForm = HeroEdit.handleSubmit;
      break;
    }
    case "text1": {
      const TextEdit = EditTextCard({ card, cardIndex });
      formContent = TextEdit.formContent;
      previewContent = TextEdit.previewContent;
      toggleTheme = TextEdit.shuffleColors;
      handleSubmitForm = TextEdit.handleSubmit;
      break;
    }
    case "link1":
    case "link2":
    case "link3":
    case "link4":
    case "link5":
    case "link6": {
      const LinkEdit = EditLinkCard({ card, cardIndex });
      formContent = LinkEdit.formContent;
      previewContent = LinkEdit.previewContent;
      switchSubTypeSelection = LinkEdit.toggleLinkType;
      toggleTheme = LinkEdit.shuffleColors;
      handleSubmitForm = LinkEdit.handleSubmit;
      break;
    }
    case "image1":
    case "image2":
    case "image3":
    case "image4":
    case "image5": {
      const ImageEdit = EditImageCard({ card, cardIndex });
      formContent = ImageEdit.formContent;
      previewContent = ImageEdit.previewContent;
      switchSubTypeSelection = ImageEdit.toggleImageType;
      toggleTheme = ImageEdit.shuffleColors;
      handleSubmitForm = ImageEdit.handleSubmit;
      break;
    }
    case "feature1":
    case "feature2": {
      const FeatureEdit = EditFeatureCard({ card, cardIndex });
      formContent = FeatureEdit.formContent;
      previewContent = FeatureEdit.previewContent;
      switchSubTypeSelection = FeatureEdit.toggleType;
      toggleTheme = FeatureEdit.shuffleColors;
      handleSubmitForm = FeatureEdit.handleSubmit;
      break;
    }
    case "faq1":
    case "faq2": {
      const FaqEdit = EditFaqCard({ card, cardIndex });
      formContent = FaqEdit.formContent;
      previewContent = FaqEdit.previewContent;
      switchSubTypeSelection = FaqEdit.toggleType;
      toggleTheme = FaqEdit.shuffleColors;
      handleSubmitForm = FaqEdit.handleSubmit;
      break;
    }
    case "whatsapp1":
    case "whatsapp2": {
      const WhatsappEdit = EditWhatsappCard({ card, cardIndex });
      formContent = WhatsappEdit.formContent;
      previewContent = WhatsappEdit.previewContent;
      switchSubTypeSelection = WhatsappEdit.toggleType;
      toggleTheme = WhatsappEdit.shuffleColors;
      handleSubmitForm = WhatsappEdit.handleSubmit;
      break;
    }
    case "tipping1":
    case "tipping2": {
      const TippingEdit = EditTippingCard({ card, cardIndex });
      formContent = TippingEdit.formContent;
      previewContent = TippingEdit.previewContent;
      switchSubTypeSelection = TippingEdit.toggleType;
      toggleTheme = TippingEdit.shuffleColors;
      handleSubmitForm = TippingEdit.handleSubmit;
      break;
    }
    default:
      formContent = <div>Unsupported card type</div>;
      previewContent = <div>Unsupported card type</div>;
      toggleTheme = () => {};
      handleSubmitForm = () => async () => {}; // No-op for unsupported cards
      break;
  }


  // Create helper to convert validated data back to FormData for compatibility with existing API
  const createFormDataFromObject = (data: Record<string, unknown>): FormData => {
    const formData = new FormData();
    
    // Special keys that should be JSON stringified instead of flattened
    const jsonKeys = new Set(['colors', 'amounts']);
    
    // Flatten nested objects with dot notation for FormData compatibility
    const flattenObject = (obj: Record<string, unknown>, prefix = ''): void => {
      for (const [key, value] of Object.entries(obj)) {
        if (value === null || value === undefined) continue;
        
        const fullKey = prefix ? `${prefix}.${key}` : key;
        
        // Special handling for array fields - convert to indexed fields
        if (key === 'faqs' && Array.isArray(value)) {
          value.forEach((faq: { question?: string; answer?: string }, index: number) => {
            formData.set(`faqQuestion${index}`, String(faq.question || ''));
            formData.set(`faqAnswer${index}`, String(faq.answer || ''));
          });
        } else if (key === 'images' && Array.isArray(value)) {
          value.forEach((img: { image?: { src?: string; alt?: string }; caption?: string }, index: number) => {
            formData.set(`imageSrc${index}`, String(img.image?.src || ''));
            formData.set(`imageAlt${index}`, String(img.image?.alt || ''));
            formData.set(`imageCaption${index}`, String(img.caption || ''));
          });
        } else if (key === 'links' && Array.isArray(value)) {
          value.forEach((link: { title?: string; url?: string; description?: string; image?: { src?: string; alt?: string } }, index: number) => {
            formData.set(`linkTitle${index}`, String(link.title || ''));
            formData.set(`linkUrl${index}`, String(link.url || ''));
            formData.set(`linkDescription${index}`, String(link.description || ''));
            formData.set(`linkImageSrc${index}`, String(link.image?.src || ''));
            formData.set(`linkImageAlt${index}`, String(link.image?.alt || ''));
          });
        } else if (key === 'features' && Array.isArray(value)) {
          value.forEach((feature: { title?: string; description?: string; image?: string }, index: number) => {
            formData.set(`featureTitle${index}`, String(feature.title || ''));
            formData.set(`featureDescription${index}`, String(feature.description || ''));
            formData.set(`featureImage${index}`, String(feature.image || ''));
          });
        }
        // Check if this key should be JSON stringified
        else if (jsonKeys.has(key)) {
          formData.set(fullKey, JSON.stringify(value));
        } else if (typeof value === 'object' && !Array.isArray(value)) {
          // Recursively flatten nested objects (except special keys)
          flattenObject(value as Record<string, unknown>, fullKey);
        } else {
          // Set primitive values or arrays (arrays get JSON.stringify)
          if (Array.isArray(value)) {
            formData.set(fullKey, JSON.stringify(value));
          } else {
            formData.set(fullKey, String(value));
          }
        }
      }
    };
    
    flattenObject(data);
    return formData;
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    setIsSubmitting(true);
    
    try {
      // Track if the submission was successful
      let submissionSuccessful = false;
      
      // Use react-hook-form's handleSubmit for validation
      await handleSubmitForm(async (validatedData) => {
        const formData = createFormDataFromObject(validatedData);
        await onSubmit?.(formData);
        submissionSuccessful = true;
      })();
      
      // If we reach here and submission was successful, show success toast
      if (submissionSuccessful) {
        toast({
          description: "Card updated successfully",
        });
        navigate({ to: "/" });
      } else {
        // Validation failed, show error toast
        toast({
          description: "Please fix the validation errors and try again.",
          variant: "destructive",
        });
      }
    } catch (e) {
      captureErrorAndToast(e, toast, {
        description: `${e}`
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <TabsWithFab
      formContent={formContent}
      previewContent={previewContent}
      switchSubTypeSelection={switchSubTypeSelection}
      switchThemeSelection={toggleTheme}
      onFormInputChange={onFormInputChange}
      onSubmit={handleSubmit}
      isSubmitting={isSubmitting}
      formId="edit-form"
      submitLabel="Publish"
    />
  );
}
